import { CloudOff, RefreshCcw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useNetworkStatus } from '@/lib/offline/useNetworkStatus';
import { usePendingMutations } from '@/lib/offline/usePendingMutations';
import { cn } from '@/lib/utils';

/**
 * Compact status pill rendered in the topbar. Three visual states:
 *
 *   1. online + no queue     → hidden (silent in the happy path).
 *   2. offline               → red pill with WifiOff + "Offline".
 *   3. online + queue > 0    → amber pill with RefreshCcw + "N pending".
 *   4. failed > 0            → red pill with CloudOff + "N failed".
 *
 * The component intentionally has no actions — F11.4 wires the click
 * handler to open the mutation-queue page.
 */
export function NetworkStatusBadge() {
  const { t } = useTranslation();
  const { online } = useNetworkStatus();
  const { pending, failed } = usePendingMutations();

  if (online && pending === 0 && failed === 0) return null;

  // Whenever the queue has any item (pending or failed), the badge
  // links to /offline-queue so the operator can review/replay/drop.
  // The pure "offline, no queue" state stays a passive label.
  const hasQueueItems = pending > 0 || failed > 0;

  const baseClass =
    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium';
  const linkClass = 'hover:opacity-90';

  if (failed > 0) {
    return (
      <Link
        to="/offline-queue"
        className={cn(baseClass, 'bg-red-100 text-red-700', linkClass)}
        role="status"
        aria-live="polite"
      >
        <CloudOff aria-hidden className="h-3.5 w-3.5" />
        {t('network.failed', { count: failed })}
      </Link>
    );
  }

  if (!online) {
    if (hasQueueItems) {
      return (
        <Link
          to="/offline-queue"
          className={cn(baseClass, 'bg-red-100 text-red-700', linkClass)}
          role="status"
          aria-live="polite"
        >
          <WifiOff aria-hidden className="h-3.5 w-3.5" />
          {t('network.offline')}
        </Link>
      );
    }
    return (
      <span
        className={cn(baseClass, 'bg-red-100 text-red-700')}
        role="status"
        aria-live="polite"
      >
        <WifiOff aria-hidden className="h-3.5 w-3.5" />
        {t('network.offline')}
      </span>
    );
  }

  return (
    <Link
      to="/offline-queue"
      className={cn(baseClass, 'bg-amber-100 text-amber-800', linkClass)}
      role="status"
      aria-live="polite"
    >
      <RefreshCcw aria-hidden className="h-3.5 w-3.5" />
      {t('network.pending', { count: pending })}
    </Link>
  );
}
